<template>
  <div class="mt-2 px-6 template-job-profiles-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="pageLoading || listLoading"
      is-requisition
    />

    <div
      v-if="!pageLoading && !listLoading"
    >
      <div class="d-flex justify-end mb-4">
        <v-btn
          color="primary"
          variant="flat"
          rounded="lg"
          @click="openCreateTemplateModal"
        >
          Create Template
        </v-btn>
      </div>

      <NoDataBlock
        v-if="!jobProfileTemplates.length"
        label="Template"
        document-type="Job Profiles"
        :show-image="false"
      >
        <template #description>
          There is no Job Profile data available for your selection.
        </template>
      </NoDataBlock>
      <div v-else>
        <StickyListHeader
          class="template-list-item-wrapper pl-4"
          document-class="item-job-profile-template"
          :header-columns="headerColumns"
          :selected-sort="selectedSort"
          :sort-direction="sortDirection"
          @submit-sort="submitSort"
        />
        <LibraryList>
          <LibraryListItemWrapper
            v-for="document in jobProfileTemplates"
            :key="document.ulid"
            class="template-list-item-wrapper"
          >
            <router-link
              :to="{ query: { preview: document.ulid, type: 'jobProfile' } }"
            >
              <TemplateJobProfileItem
                :ulid="document.ulid"
                :title="document.title"
                :job-level="document.job_level"
                :language="document.primary_language"
                :last-revised="document.created_at"
                class="item-job-profile-template pl-4"
              />
            </router-link>
          </LibraryListItemWrapper>
        </LibraryList>
      </div>
    </div>

    <StickyPagination
      v-if="totalPages > 1"
      :model-value="page"
      :length="totalPages"
      @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router })"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useJobProfileTemplates } from '@/stores/jobProfiles/jobProfileTemplates';
import { useFilters } from '@/stores/filters/filters';
import { useModals } from '@/stores/modals/modals';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import TemplateJobProfileItem from '@/components/jobProfiles/profileListItems/TemplateJobProfileItem.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import { stickyHeadersJobProfiles } from '@/helpers/constants/stickyHeaders';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { jobProfileTemplates } = storeToRefs(useJobProfileTemplates());
const { loadJobProfileTemplates } = useJobProfileTemplates();
const {
  setFilterConfig,
  submitSubMenu,
  sendFilterRequest,
  setSearchTerm,
} = useFilters();
const { selectedSubMenuOption } = storeToRefs(useSubMenu());
const { setSelectedSubMenuOption } = useSubMenu();
const { setModal } = useModals();
const { totalPages, page } = storeToRefs(useAppPagination());
const { open } = storeToRefs(useFilterDrawer());

const headerColumns = computed(() => {
  const templateHeaders = [
    'title',
    'level',
    'language',
    'lastRevised',
    'notes',
  ];

  return templateHeaders.map((header) => stickyHeadersJobProfiles[header]);
});

const selectedSort = computed(() => {
  const fallback = route.query?.sort || 'created_at';
  return (selectedSubMenuOption.value || fallback).replace('-', '');
});

const sortDirection = computed(() => (
  (selectedSubMenuOption.value || route.query?.sort || 'created_at').startsWith('-')
    ? 'desc'
    : 'asc'
));

const openCreateTemplateModal = () => {
  setModal({ field: 'createProfileTemplate', value: true });
};

const submitSort = (selectedId) => {
  const current = selectedSubMenuOption.value || route.query?.sort || 'created_at';
  const currentBase = current.replace('-', '');

  if (selectedId === '__select') return;

  if (selectedId === currentBase) {
    const optionId = current.startsWith('-') ? selectedId : `-${selectedId}`;

    submitSubMenu({ optionId, router });
  } else {
    submitSubMenu({ optionId: selectedId, router });
  }
};

const loadPage = async (params) => {
  await loadJobProfileTemplates(params);
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobProfiles',
    showMyJobs: false,
    datepicker: false,
    subMenuType: 'chip',
  });
  open.value = false;

  if (route.query?.q) setSearchTerm(route.query.q);

  await loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    searchTerm: route.query?.q || '',
  });

  emit('track-page-load', { page: 'Job Profile Templates: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  setFilterConfig({
    subMenuType: null,
  });
  setSelectedSubMenuOption('');
});
</script>

<style lang="less" scoped>
.template-list-item-wrapper {
  display: grid;
  grid-template-columns: 1fr;
}

.item-job-profile-template, :deep(.item-job-profile-template) {
  grid-template-columns: minmax(150px, 250px) 120px 80px 120px minmax(350px, 1fr);
}
</style>
